import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, MailCheck, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const KINDS = ["", "warning", "gift_reminder", "broadcast", "broadcast_test", "admin_notice", "admin_message", "password_reset", "other"];

const KIND_TONE = {
  warning: "bg-amber-100 text-amber-800",
  gift_reminder: "bg-purple-100 text-purple-800",
  broadcast: "bg-blue-100 text-blue-800",
  broadcast_test: "bg-blue-50 text-blue-700",
  admin_notice: "bg-cream-2 text-espresso/70",
  admin_message: "bg-teal-100 text-teal-800",
  password_reset: "bg-slate-100 text-slate-700",
  other: "bg-gray-100 text-gray-600",
};

function Tile({ label, value, tone = "", testId }) {
  return (
    <div className={`bg-white rounded-xl border border-app-border px-4 py-3 ${tone}`} data-testid={testId}>
      <div className="text-2xl font-heading font-semibold text-espresso leading-none">{value}</div>
      <div className="text-[11px] text-espresso/60 mt-1">{label}</div>
    </div>
  );
}

export const EmailLog = () => {
  const { t } = useI18n();
  const [kind, setKind] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/email-log?kind=${kind}&limit=150`).then(({ data: d }) => setData(d)).catch(() => setData({ rows: [], summary_30d: {} }));
  }, [kind]);

  const s = data?.summary_30d || {};
  const kindLabel = (k) => t(`elog.kind_${k}`);

  return (
    <section className="space-y-3" data-testid="email-log">
      <div>
        <h2 className="font-heading text-xl font-semibold text-espresso flex items-center gap-2"><MailCheck className="w-5 h-5 text-olive" />{t("elog.title")}</h2>
        <p className="text-xs text-espresso/60">{t("elog.sub")}</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Tile label={t("elog.total_30d")} value={s.total ?? 0} testId="elog-total" />
        <Tile label={t("elog.delivered")} value={s.delivered ?? 0} tone="border-green-200 bg-green-50" testId="elog-delivered" />
        <Tile label={t("elog.failed")} value={s.failed ?? 0} tone={s.failed ? "border-red-200 bg-red-50" : ""} testId="elog-failed" />
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="elog-filters">
        {KINDS.map((k) => (
          <button
            key={k || "all"}
            type="button"
            onClick={() => setKind(k)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
              kind === k ? "bg-olive text-white border-olive" : "bg-white text-espresso/70 border-app-border hover:border-olive"
            }`}
            data-testid={`elog-filter-${k || "all"}`}
          >
            {k ? kindLabel(k) : t("elog.all")}
            {k && s.by_kind?.[k] ? ` (${s.by_kind[k].delivered + s.by_kind[k].failed})` : ""}
          </button>
        ))}
      </div>
      {data === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
      ) : data.rows.length === 0 ? (
        <div className="text-sm text-espresso/50 bg-white rounded-xl border border-app-border p-4" data-testid="elog-empty">{t("elog.empty")}</div>
      ) : (
        <div className="bg-white rounded-xl border border-app-border overflow-x-auto" data-testid="elog-rows">
          <table className="w-full text-xs">
            <thead className="bg-cream-2 text-[10px] uppercase tracking-wider text-espresso/60">
              <tr>
                <th className="text-start px-3 py-2">{t("elog.col_time")}</th>
                <th className="text-start px-3 py-2">{t("elog.col_kind")}</th>
                <th className="text-start px-3 py-2">{t("elog.col_to")}</th>
                <th className="text-start px-3 py-2">{t("elog.col_subject")}</th>
                <th className="text-start px-3 py-2">{t("elog.col_status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-app-border">
              {data.rows.map((r, i) => (
                <tr key={i} data-testid={`elog-row-${i}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-espresso/70">{new Date(r.sent_at).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${KIND_TONE[r.kind] || KIND_TONE.other}`}>
                      {kindLabel(r.kind)}{r.kind === "warning" && r.meta?.stage ? ` ${r.meta.stage}` : ""}
                    </span>
                  </td>
                  <td className="px-3 py-2 truncate max-w-[200px]" dir="ltr">{r.to}</td>
                  <td className="px-3 py-2 truncate max-w-[280px] text-espresso/80">{r.subject}</td>
                  <td className="px-3 py-2">
                    {r.ok ? (
                      <span className="inline-flex items-center gap-1 text-green-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />{t("elog.ok")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-700 font-semibold" title={r.error}><XCircle className="w-3.5 h-3.5" />{t("elog.fail")}{r.error ? ` · ${r.error}` : ""}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
